import {
  queryKeyBounds,
  queryKeyDestinationId,
  queryKeyDestinationName,
  queryKeyPages,
} from 'constants/url'
import { average, sortBy } from 'lib/array/arrayUtils'
import { getMetresDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'

type SelectedItemIds = Array<{
  id: string
}>

export function encodeOfferIds(experienceItems: Array<App.ExperienceOffer>) {
  return experienceItems
    .map(experienceItem => experienceItem.id)
    .join(',')
}

export function decodeOfferIds(idsParam?: string): SelectedItemIds {
  if (idsParam) {
    return idsParam
      .split(',')
      .map(id => {
        return { id }
      })
  }
  return []
}

/*
  Groups offers that either have the same property, or are in the same building, or otherwise are so
  close together that it's not feasible to show separate markers for them.
  To make this algorithm more efficient, first the offers are sorted by their latitude.
  That means for each offer, instead of comparing it against every other offer (which would have
  O(n^2) time complexity), we can stop comparing once we reach an offer that's too far away in
  latitude - each subsequent offer will also be too far away, so we can break that loop and move on.
*/
export function groupOffersAtSameCoordinates(experiencesItems: Array<App.ExperienceOffer>): Array<Array<App.ExperienceOffer>> {
  const metresThreshold = 10
  const latSortedOffers = sortBy(experiencesItems, experienceItem => experienceItem.location.latitude, 'desc')

  const groupedExperiencesSet = new Set<App.ExperienceOffer>()
  const groups: Array<Array<App.ExperienceOffer>> = []

  for (let i = 0; i < latSortedOffers.length; i++) {
    const referenceOffer = latSortedOffers[i]

    if (groupedExperiencesSet.has(referenceOffer)) {
      continue
    }

    groupedExperiencesSet.add(referenceOffer)

    const group = [referenceOffer]
    groups.push(group)

    const { latitude: lat1, longitude: lon1 } = referenceOffer.location

    // Find offers that are within the threshold distance from `referenceOffer`
    for (let j = i + 1; j < latSortedOffers.length; j++) {
      const thisOffer = latSortedOffers[j]
      const { latitude: lat2, longitude: lon2 } = thisOffer.location

      // Use latitude heuristic to see if we can break the loop
      if (getMetresDistanceFromLatLong(lat1, 0, lat2, 0) >= metresThreshold) {
        break
      }

      // Check if the offer has already been put in a different group
      if (groupedExperiencesSet.has(thisOffer)) {
        continue
      }

      if (getMetresDistanceFromLatLong(lat1, lon1, lat2, lon2) < metresThreshold) {
        group.push(thisOffer)
        groupedExperiencesSet.add(thisOffer)
      }
    }
  }

  return groups
}

function hasValidLocation(experienceItem?: App.ExperienceOffer): boolean {
  return !!experienceItem?.location &&
    typeof experienceItem.location.longitude === 'number' &&
    typeof experienceItem.location.latitude === 'number'
}

function getMarkerByOfferGroup(experienceItems: Array<App.ExperienceOffer>): App.SearchExperienceMapMarker {
  const latitude = average(experienceItems, experienceItem => experienceItem.location.latitude)
  const longitude = average(experienceItems, experienceItem => experienceItem.location.longitude)

  return {
    id: experienceItems.map(experienceItem => experienceItem.id).join(','),
    latitude: parseFloat((latitude).toFixed(6)),
    longitude: parseFloat((longitude).toFixed(6)),
    experienceItems,
  }
}

export function getMarkersByOffers(experiencesItems: Array<App.ExperienceOffer>): Array<App.SearchExperienceMapMarker> {
  const ItemsGroups = groupOffersAtSameCoordinates(experiencesItems.filter(hasValidLocation))
  return ItemsGroups.map(offers => getMarkerByOfferGroup(offers))
}

export function listHasPlaceId(list: App.ApiCallState<{ ids: Array<string>, placeId: string | undefined }>): list is App.ApiCallState<{ ids: Array<string>, placeId: string }> {
  return !!list.placeId
}

/**
 * Hotel map encodeSearchParams replaces destination with bounds. Restore the
 * destination when switching back to the experience list.
 */
export function getExperienceListViewSearch(windowSearch: string, previousDestination?: string): string {
  const currentSearchParams = new URLSearchParams(windowSearch)
  currentSearchParams.delete(queryKeyBounds)
  currentSearchParams.delete(queryKeyPages)

  const hasCurrentLocation = currentSearchParams.has(queryKeyDestinationId)
  if (!hasCurrentLocation && previousDestination) {
    const previousSearchParams = new URLSearchParams(previousDestination)
    const destinationId = previousSearchParams.get(queryKeyDestinationId)
    const destinationName = previousSearchParams.get(queryKeyDestinationName)
    if (destinationId) currentSearchParams.set(queryKeyDestinationId, destinationId)
    if (destinationName) currentSearchParams.set(queryKeyDestinationName, destinationName)
  }

  const search = currentSearchParams.toString()
  return search ? `?${search}` : ''
}

/** Place used for list-enriched / stream list key after a pan dropped destinationId from the URL. */
export function getExperienceMapSearchPlaceId(
  destinationId: string | undefined,
  previousDestination?: string,
): string | undefined {
  if (destinationId) return destinationId
  if (!previousDestination) return undefined
  return new URLSearchParams(previousDestination).get(queryKeyDestinationId) ?? undefined
}
